import { useRef, useState, useEffect } from "react";
import { useRoom } from "../room/useRoom";
import type { RoomPeer } from "../room/peer-state";

export function PeerRow({ peer }: { peer: RoomPeer }) {
  const { bus } = useRoom();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const connected = peer.status === "connected";

  useEffect(() => {
    const off = bus.on("unread-counts-updated", (counts) => {
      setUnreadCount(counts[peer.peerId] || 0);
    });
    return off;
  }, [bus, peer.peerId]);

  const handleClick = () => {
    if (!connected) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      bus.emit("files-selected", { to: peer.peerId, files: Array.from(files) });
    }
    e.target.value = ""; // Reset input selection
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!connected) return;
    bus.emit("ui-request-send-text", { to: peer.peerId, displayName: peer.displayName || peer.peerId });
  };

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!connected) return;
    bus.emit("ui-request-send-text", { to: peer.peerId, displayName: peer.displayName || peer.peerId });
  };

  return (
    <li
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-3 py-2 transition-all ${
        connected
          ? "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--color-accent)] active:bg-[var(--border)]"
          : "border-[var(--border)] bg-[var(--surface)]/50 opacity-60 cursor-not-allowed"
      }`}
    >
      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />
      <span
        className={"h-2.5 w-2.5 shrink-0 rounded-full " + (connected ? "bg-[var(--color-accent)]" : "bg-[var(--muted)]")}
        title={connected ? "Connected" : "Connecting…"}
        aria-hidden
      />
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{peer.displayName || peer.peerId}</span>
        <span className="truncate text-xs text-[var(--muted)]">
          {peer.deviceName || "Unknown device"} · {connected ? "ready" : "connecting…"}
        </span>
      </span>
      {connected && (
        <button
          type="button"
          onClick={handleChatClick}
          className="relative ml-auto p-1.5 text-[var(--muted)] hover:text-[var(--color-accent)] hover:bg-[var(--border)] rounded-full transition-colors duration-150"
          title="Chat with peer"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse">
              {unreadCount}
            </span>
          )}
        </button>
      )}
    </li>
  );
}

